import type { MediaItem, MediaSeries, MediaType } from "../types/subsplash";
import { getYear, getYearQuarter } from "./dateTime";

/**
 * MediaItem Data Access
 * -----------------------------------------------------------------------------
 */

/**
 * Given a MediaItem, return its embedded MediaSeries.
 */
export function embeddedMediaSeries(item: MediaItem): MediaSeries | undefined {
  return item._embedded?.["media-series"];
}

/**
 * Given a MediaItem or a MediaSeries, return its embedded square image URL.
 */
export function embeddedSquareImageUrl(item: MediaItem | MediaSeries) {
  return item._embedded?.images?.find((image) => image.type === "square")
    ?._links?.related.href;
}

/**
 * Given a MediaItem or a MediaSeries, return its embedded wide image URL.
 */
export function embeddedWideImageUrl(item: MediaItem | MediaSeries) {
  return item._embedded?.images?.find((image) => image.type === "wide")?._links
    ?.related.href;
}

/**
 * Given a MediaItem, determine a MediaType.
 */
export function determineMediaType(item: MediaItem): MediaType {
  if (item._embedded?.video) {
    return "Video";
  }
  if (item._embedded?.audio) {
    return "Audio";
  }
  if (item._embedded?.document) {
    return "Reading";
  }
  return "Media"; // default
}

/**
 * MediaItem Array Range Processing
 * -----------------------------------------------------------------------------
 */

export const areItemsSameQuarter = (item1: MediaItem, item2: MediaItem) => {
  const item1Date = item1?.date ? new Date(item1.date) : undefined;
  const item2Date = item2?.date ? new Date(item2.date) : undefined;
  return (
    !!item1Date &&
    !!item2Date &&
    getYearQuarter(item1Date) === getYearQuarter(item2Date)
  );
};

export const areItemsSameYear = (item1: MediaItem, item2: MediaItem) => {
  const item1Date = item1?.date ? new Date(item1.date) : undefined;
  const item2Date = item2?.date ? new Date(item2.date) : undefined;
  return (
    !!item1Date && !!item2Date && getYear(item1Date) === getYear(item2Date)
  );
};

interface Range {
  /** range title, e.g. "2024-Q3", "2024" */
  title: string;
  /** items in this range */
  items: MediaItem[];
}

/**
 * Given a _sorted_ array of MediaItems, divide them into ranges.
 */
export function divideSortedItemsIntoRanges(
  /** pre-sorted array of MediaItems */
  items: MediaItem[],
  /** function to determine if two items are in the same range */
  areItemsInSameRange: (item1: MediaItem, item2: MediaItem) => boolean,
  /**
   * function to create a title string for a particular range,
   * given one item in that range
   */
  getRangeTitle: (item: MediaItem) => string,
): Range[] {
  return items.reduce(
    (acc, mediaItem, index) => {
      const previousItem = index > 0 ? items[index - 1] : undefined;
      if (
        acc.length > 0 &&
        !!previousItem &&
        areItemsInSameRange(mediaItem, previousItem)
      ) {
        // add to existing range
        acc[acc.length - 1].items.push(mediaItem);
      } else {
        // start a new range
        acc.push({
          title: getRangeTitle(mediaItem),
          items: [mediaItem],
        });
      }
      return acc;
    },
    [] as Range[], // accumulator initial value
  );
}
